import type { DatabaseModel } from './database';
import type { OptionColor, PropertyDef, PropertyType, RowData, SelectOption, ViewDef } from './db-types';
import type { PageParent } from './types';
import { OPTION_COLORS } from './db-types';
import { cellText, cellValue, type ValueContext } from './db-values';
import { matchesFilter } from './filter';
import { sortRows } from './sort';

/** Parses CSV (RFC 4180: quoted fields, escaped quotes, newlines inside quotes, CRLF). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const src = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += c;
      continue;
    }
    if (c === '"' && field === '') quoted = true;
    else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.length > 1 || r[0] !== '');
}

function csvField(v: string): string {
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map(csvField).join(',')).join('\n') + '\n';
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[ T](\d{2}:\d{2}))?$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const LONG_DATE = /^[A-Z][a-z]+ \d{1,2}, \d{4}$/;

/** Parses the date formats CSV exports commonly use into `YYYY-MM-DD` (and `HH:MM`). */
export function parseDateText(v: string): { start: string; time?: string } | null {
  const t = v.trim();
  const iso = ISO_DATE.exec(t);
  if (iso) return { start: t.slice(0, 10), ...(iso[1] ? { time: iso[1] } : {}) };
  const us = US_DATE.exec(t);
  if (us) return { start: `${us[3]}-${us[1]!.padStart(2, '0')}-${us[2]!.padStart(2, '0')}` };
  if (LONG_DATE.test(t)) {
    const d = new Date(`${t} 12:00`);
    if (!Number.isNaN(d.getTime())) return { start: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` };
  }
  return null;
}

const NUMBER = /^-?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/;
const URL_RE = /^https?:\/\/\S+$/i;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CHECK = /^(yes|no|true|false|checked|unchecked)$/i;

/**
 * Infers a property type from a column's values (P-522): number, date, checkbox, URL, email, select
 * (few distinct values that repeat), multi-select (comma-separated values from a small set), else text.
 */
export function inferColumnType(values: string[]): PropertyType {
  const filled = values.map((v) => v.trim()).filter(Boolean);
  if (!filled.length) return 'text';
  const all = (re: RegExp) => filled.every((v) => re.test(v));
  if (all(CHECK)) return 'checkbox';
  if (all(NUMBER)) return 'number';
  if (filled.every((v) => parseDateText(v))) return 'date';
  if (all(URL_RE)) return 'url';
  if (all(EMAIL)) return 'email';
  const distinct = new Set(filled);
  if (filled.some((v) => v.includes(',')) && filled.length >= 2) {
    const parts = filled.flatMap((v) => v.split(',').map((x) => x.trim()));
    const set = new Set(parts);
    if (set.size <= Math.max(3, parts.length / 2) && [...set].every((p) => p.length <= 30)) return 'multi_select';
  }
  if (filled.length >= 2 && distinct.size < filled.length && distinct.size <= 20 && [...distinct].every((v) => v.length <= 40)) return 'select';
  return 'text';
}

function optionsFor(values: string[], multi: boolean): SelectOption[] {
  const names = new Set<string>();
  for (const v of values) for (const n of multi ? v.split(',') : [v]) if (n.trim()) names.add(n.trim());
  const colors = OPTION_COLORS.filter((c) => c !== 'default') as OptionColor[];
  return [...names].map((name, i) => ({ id: `opt-${i}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`, name, color: colors[i % colors.length]! }));
}

/** Converts a CSV cell into a stored value for a property. */
export function csvValue(prop: PropertyDef, raw: string): unknown {
  const v = raw.trim();
  if (!v) return prop.type === 'checkbox' ? false : prop.type === 'multi_select' ? [] : null;
  switch (prop.type) {
    case 'number':
      return Number(v.replace(/,/g, ''));
    case 'checkbox':
      return /^(yes|true|checked)$/i.test(v);
    case 'date':
      return parseDateText(v);
    case 'select':
      return prop.options?.find((o) => o.name === v)?.id ?? null;
    case 'multi_select':
      return v
        .split(',')
        .map((n) => prop.options?.find((o) => o.name === n.trim())?.id)
        .filter(Boolean);
    default:
      return v;
  }
}

/**
 * Imports a CSV as a new database (P-522): the first row holds the column names, the first column
 * becomes the title, the others get inferred types. Returns the database id.
 */
export function importCsv(model: DatabaseModel, parent: PageParent, title: string, text: string, opts: { id?: string } = {}): string {
  const rows = parseCsv(text);
  const header = rows[0] ?? ['Name'];
  const body = rows.slice(1);
  const dbId = model.createDatabase({ parent, title, withRows: false, ...(opts.id ? { id: opts.id } : {}) });
  for (const p of model.schema(dbId)) if (p.type !== 'title') model.deleteProperty(dbId, p.id);
  model.updateProperty(dbId, 'title', { name: header[0] || 'Name' });
  const props: PropertyDef[] = [];
  header.slice(1).forEach((name, i) => {
    const values = body.map((r) => r[i + 1] ?? '');
    const type = inferColumnType(values);
    const def: Omit<PropertyDef, 'id'> = { name: name || `Column ${i + 2}`, type, ...(type === 'select' || type === 'multi_select' ? { options: optionsFor(values, type === 'multi_select') } : {}) };
    const id = model.addProperty(dbId, def);
    props.push({ ...def, id });
  });
  for (const r of body) {
    const values: Record<string, unknown> = {};
    props.forEach((p, i) => {
      values[p.id] = csvValue(p, r[i + 1] ?? '');
    });
    model.addRow(dbId, { title: r[0] ?? '', values });
  }
  return dbId;
}

/** Rows of a database (or one view's rows, in view order) as CSV with the given properties (P-527). */
export function exportCsv(ctx: ValueContext, dbId: string, props: PropertyDef[], rows: RowData[]): string {
  const cols = props.filter((p) => p.type !== 'button');
  return toCsv([cols.map((p) => p.name), ...rows.map((r) => cols.map((p) => cellText(ctx, dbId, r, p)))]);
}

/**
 * A database as CSV (P-527): with `view`, only the rows that view shows (filter and sorts) and its
 * visible properties in view order; without, every row and property.
 */
export function databaseCsv(model: DatabaseModel, dbId: string, me: string, view?: ViewDef | null, now = new Date()): string {
  const ctx = model.valueContext(me, () => now);
  const schema = model.schema(dbId);
  let rows = model.rows(dbId);
  let props = schema;
  if (view) {
    const valueOf = (row: RowData, prop: PropertyDef) => cellValue(ctx, dbId, row, prop);
    rows = sortRows(
      rows.filter((r) => matchesFilter(view.filter, r, schema, valueOf, { me, now })),
      view.sorts,
      schema,
      valueOf,
      ctx.memberName,
    );
    const title = schema.find((p) => p.type === 'title');
    props = [...(title ? [title] : []), ...view.visible.map((id) => schema.find((p) => p.id === id)).filter((p): p is PropertyDef => !!p && p.type !== 'title')];
  }
  return exportCsv(ctx, dbId, props, rows);
}
